// Centralized analytics tracking layer for LYBL.
// Flow: browser dataLayer -> GTM web container -> your sGTM server -> GA4 + Meta CAPI.
// NO Meta Pixel browser JS is loaded here (by design).
//
// ---------------------------------------------------------------------------
// EVENT MAP  (EDIT HERE to change how actions map to Meta/GA4 event names)
// key      -> the internal action name we call track() with
// meta     -> the Meta (Conversions API) standard event name
// ga4      -> the GA4 event name
// ---------------------------------------------------------------------------
export const EVENTS = {
  page_view: { meta: "PageView", ga4: "page_view" },
  view_content: { meta: "ViewContent", ga4: "view_item" },
  schedule: { meta: "Schedule", ga4: "schedule_consult" },
  subscribe: { meta: "CompleteRegistration", ga4: "sign_up" },
  dosha: { meta: "Lead", ga4: "generate_lead" },
};

// --- cookie helpers ---------------------------------------------------------
const getCookie = (name) =>
  document.cookie
    .split("; ")
    .find((v) => v.startsWith(`${name}=`))
    ?.split("=")
    .slice(1)
    .join("=");

const setCookie = (name, value, days = 90) => {
  const maxAge = days * 24 * 60 * 60;
  document.cookie = `${name}=${value}; Path=/; Max-Age=${maxAge}; SameSite=Lax; Secure`;
};

// _fbp / _fbc are needed for Meta match quality. Without the Pixel they are not
// created automatically, so we create/read them here.
export function ensureFbp() {
  let fbp = getCookie("_fbp");
  if (!fbp) {
    fbp = `fb.1.${Date.now()}.${Math.floor(Math.random() * 1e10)}`;
    setCookie("_fbp", fbp);
  }
  return fbp;
}

export function captureFbc() {
  const fbclid = new URLSearchParams(window.location.search).get("fbclid");
  if (fbclid) {
    const fbc = `fb.1.${Date.now()}.${fbclid}`;
    setCookie("_fbc", fbc);
    return fbc;
  }
  return getCookie("_fbc");
}

// --- utils ------------------------------------------------------------------
export function newEventId() {
  if (window.crypto?.randomUUID) return window.crypto.randomUUID();
  return "xxxxxxxxxxxx4xxxyxxxxxxxxxxxxxxx".replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    return (c === "x" ? r : (r & 0x3) | 0x8).toString(16);
  });
}

// SHA-256 hash (lowercase + trimmed) for PII like email, per Meta requirements.
export async function sha256(value) {
  if (!value) return undefined;
  const normalized = String(value).trim().toLowerCase();
  const buf = await crypto.subtle.digest(
    "SHA-256",
    new TextEncoder().encode(normalized)
  );
  return Array.from(new Uint8Array(buf))
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("");
}

// --- GTM loader -------------------------------------------------------------
// Production hostnames allowed to load GTM & send data. Preview hosts
// (*.preview.emergentagent.com) are intentionally NOT listed, so no preview
// data ever reaches GA4/Meta. Override/extend via REACT_APP_ANALYTICS_HOSTS.
const PROD_HOSTS = (process.env.REACT_APP_ANALYTICS_HOSTS || "care-connect-775.emergent.host")
  .split(",")
  .map((s) => s.trim())
  .filter(Boolean);

export function isAnalyticsHost() {
  if (typeof window === "undefined") return false;
  const h = window.location.hostname;
  return PROD_HOSTS.some((host) => h === host || h.endsWith(`.${host}`));
}

let gtmLoaded = false;
export function initGtm(gtmId) {
  if (typeof window === "undefined") return;
  window.dataLayer = window.dataLayer || [];

  const validId = gtmId && /^GTM-[A-Z0-9]+$/.test(gtmId) && gtmId !== "GTM-XXXXXXX";
  const allowedHost = isAnalyticsHost();

  // Load GTM ONLY on a real production host AND with a real container id.
  if (validId && allowedHost && !gtmLoaded) {
    window.dataLayer.push({ "gtm.start": new Date().getTime(), event: "gtm.js" });
    const f = document.getElementsByTagName("script")[0];
    const j = document.createElement("script");
    j.async = true;
    j.src = `https://www.googletagmanager.com/gtm.js?id=${gtmId}`;
    f.parentNode.insertBefore(j, f);
    gtmLoaded = true;
  } else {
    // eslint-disable-next-line no-console
    console.info(
      `[tracking] GTM NOT loaded (host allowed: ${allowedHost}, valid id: ${validId}). ` +
        "dataLayer events queue locally but nothing is sent."
    );
  }

  initCtaDelegation();
}

// --- core track() -----------------------------------------------------------
// action: one of EVENTS keys. extra.user_data / extra.custom_data optional.
export function track(action, extra = {}) {
  const map = EVENTS[action];
  if (!map) {
    // eslint-disable-next-line no-console
    console.warn(`[tracking] unknown action "${action}"`);
    return;
  }
  window.dataLayer = window.dataLayer || [];

  const payload = {
    // `event` is what GTM triggers listen on. We use the GA4-style name.
    event: map.ga4,
    meta_event_name: map.meta,
    event_id: extra.event_id || newEventId(),
    event_time: Math.floor(Date.now() / 1000),
    event_source_url: window.location.href,
    action_source: "website",
    user_data: {
      fbp: ensureFbp(),
      fbc: captureFbc(),
      ...(extra.user_data || {}),
    },
    custom_data: extra.custom_data || {},
  };

  window.dataLayer.push(payload);
  return payload;
}

export function trackPageView() {
  return track("page_view");
}

// --- delegated CTA click tracking ------------------------------------------
// Any element with [data-evt="schedule|dosha"] fires the mapped event.
let delegationBound = false;
function initCtaDelegation() {
  if (delegationBound || typeof document === "undefined") return;
  document.addEventListener(
    "click",
    (e) => {
      const el = e.target?.closest?.("[data-evt]");
      if (!el) return;
      const action = el.getAttribute("data-evt");
      const content = el.getAttribute("data-evt-content");
      track(action, content ? { custom_data: { content_name: content } } : {});
    },
    true
  );
  delegationBound = true;
}
